/** @jsxImportSource solid-js */
import { customElement, noShadowDOM } from "solid-element";
import { Show } from "solid-js";
import { PrimaryButton } from "#palette/components/button.solid";
import { PrivateRoutes } from "../src/config/routes";
import { globalState } from "../state";

// Shims for embedded Solid web components used on this page
import "../components/shims/recoveryError.solid";
import "../components/recovery/personalEmail.solid";

import type { JSX, ValidComponent } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Card } from "#palette/components/card.solid";
import { Icons } from "#palette/components/icons.solid";

function DashboardCard(props: {
  class?: string;
  children: JSX.Element;
  title: string;
  icon: ValidComponent;
  action: JSX.Element;
}) {
  return (
    <Card class={props.class}>
      <div class="flex flex-row content-center">
        <span class="text-xl font-bold font-serif">{props.title}</span>
        <span class="[&>*]:h-8 [&>*]:w-8 text-right grow">
          <Dynamic component={props.icon} />
        </span>
      </div>
      {props.children}
      <nav class="mt-4 space-y-3 grow place-self-end content-end">
        {props.action}
      </nav>
    </Card>
  );
}

export function Dashboard() {
  const numRecords =
    globalState.client.records.length + globalState.client.incidentLogs.length;
  const numEntries = globalState.client.entries.length;

  return (
    <>
      <h1 class="text-2xl font-bold py-5">Welcome to Callisto Vault</h1>
      <p class="py-2 text-m">
        We are humbled that you have chosen to trust Callisto to help support
        you on your journey toward healing and justice, in whatever shape that
        takes for you. That may include our Matching System, our Incident Log,
        or utilizing our resources to get the support you need. We know that
        this process can be difficult. Take your time.
      </p>
      <p class="py-2 text-m font-bold">
        If you need to exit at any time, the Quick Exit button will log you out
        and take you to the Weather Channel. Be aware that if you use this
        button, you will lose any unsaved work.
      </p>
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-8 my-5">
        <DashboardCard
          title="Matching System"
          icon={Icons.Matching}
          action={
            <a href={PrivateRoutes.CREATE_WHERE} class="w-full">
              <PrimaryButton>Create a Matching Entry</PrimaryButton>
            </a>
          }
        >
          <p class="text-m text-brand-500 py-4">
            Callisto's innovative encrypted matching system allows survivors of
            sexual violence to securely report their perpetrators and get access
            to legal options if their entries match with other survivors of that
            perpetrator.
          </p>
          <p class="text-m py-4 font-bold">
            You have{" "}
            {numEntries === 0
              ? "no Matching Entries"
              : numEntries === 1
                ? "1 Matching Entry"
                : `${numEntries} Matching Entries`}
            .
          </p>
        </DashboardCard>
        <DashboardCard
          title="Incident Log"
          icon={Icons.IncidentLog}
          action={
            <a href={PrivateRoutes.ILOG_WHO_WHAT} class="w-full">
              <PrimaryButton>Create an Incident Log</PrimaryButton>
            </a>
          }
        >
          <p class="text-m text-brand-500 py-4">
            An Incident Log is a secure document with as much detail as you can
            provide about your experiences, designed so you can easily share
            those details with counselors or legal advocates or friends without
            having to get into it and re-traumatize yourself.{" "}
            <span class="font-bold">
              Incident Logs are encrypted so only you can view them; no one
              else, including Callisto staff, can decrypt the information.
            </span>
          </p>
          <p class="text-m py-4 font-bold">
            You have{" "}
            {numRecords === 0
              ? "no Incident Logs"
              : numRecords === 1
                ? "1 Incident Log"
                : `${numRecords} Incident Logs`}
            .
          </p>
        </DashboardCard>
        <DashboardCard
          title="Account Settings"
          icon={Icons.User.Settings}
          action={
            <a href={PrivateRoutes.ACCOUNT} class="w-full">
              <PrimaryButton>View / Change your Account Settings</PrimaryButton>
            </a>
          }
        >
          &nbsp;
        </DashboardCard>
        <DashboardCard
          title="Consent Settings"
          icon={Icons.User.Consent}
          action={
            <a href={PrivateRoutes.CONSENT_SETTINGS} class="w-full">
              <PrimaryButton>View / Change your Consent Settings</PrimaryButton>
            </a>
          }
        >
          <p class="text-m text-brand-500 py-4">
            See what you are consenting to.
          </p>
        </DashboardCard>
        <DashboardCard
          title="Resources"
          icon={Icons.Resources}
          action={
            <a href={PrivateRoutes.RESOURCES} class="w-full">
              <PrimaryButton>View Resources</PrimaryButton>
            </a>
          }
        >
          <p class="text-m text-brand-500 py-4">
            Callisto partners with a number of organizations to provide free
            services to survivors with accounts. You can view those here.
          </p>
        </DashboardCard>
      </div>
    </>
  );
}

customElement("main-dashboard", {}, () => {
  noShadowDOM();

  return <Dashboard />;
});
